import { useEffect, useRef, useState, type RefObject } from 'react';
import { adminSignupGateway } from '../../services/AdminSignupGateway';
import type { CompanyVerificationStatusResponse } from '../../types';
import type { StepProps } from '../AdminSignUpPage';
import { friendlyErrorMessage } from '../../../../shared/friendlyError';

const DOCUMENT_TYPES = ['Certificate of Formation', 'Articles of Incorporation', 'Business License', 'IRS EIN Letter', 'No Documents Yet'];
const MISSING_REASONS = ['Documents are still being processed', 'Recently formed business', 'Document is temporarily unavailable', 'Other'];
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

const FOCUSABLE_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Traps Tab focus inside an open modal and closes it on Escape — mirrors the approved FINAL HTML's own
 * modal behavior (backdrop click / Escape / close button all dismiss; focus never silently escapes to the
 * page underneath). Restores focus to whatever triggered the modal once it closes. */
function useModalFocusTrap(active: boolean, containerRef: RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    if (!active) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const container = containerRef.current;
    const focusable = () => Array.from(container?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? []);
    focusable()[0]?.focus();

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab') {
        const items = focusable();
        if (items.length === 0) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      previouslyFocused?.focus();
    };
  }, [active, containerRef, onClose]);
}

/**
 * Step 1B — Identity Verification. Continue requires an accepted document AND a captured live photo
 * (RegistrationService.RecomputeVerificationStatusAsync moves status to Submitted only once both exist);
 * otherwise the explicit Skip path must be used, which persists Pending/Skipped rather than a false
 * "complete" state.
 * <p>
 * Live-photo capture now matches the approved FINAL HTML's modal interaction: "Open Camera" opens a
 * `role="dialog"` capture modal (live preview, Cancel/Capture Photo, focus trap, Escape-to-close,
 * permission-denied fallback state), which closes into the inline "Live Photo Captured" success card with
 * Retake/View Photo — View Photo opens a second, simpler preview-only modal. The camera's MediaStream is
 * always stopped on Cancel, Capture, Escape, backdrop click, or unmount. None of this changes the
 * underlying security rule: a captured live photo is evidence for a human reviewer, never by itself a
 * "Verified" identity result (see CompanyIdentityVerification.Status, which this UI never sets to Verified).
 */
export interface IdentityVerificationStepProps extends StepProps {
  /** EPN-35 Partner Sign Up override — default matches Admin Sign Up's own approved copy exactly. */
  heading?: string;
  accessibleHeading?: string;
  firstSubstepLabel?: string;
}

export function IdentityVerificationStep({
  goNext,
  goBack,
  heading = 'Step 1: Company Information',
  accessibleHeading = 'Step 1: Company Information — Identity Verification',
  firstSubstepLabel = 'Company Info',
}: IdentityVerificationStepProps) {
  const [status, setStatus] = useState<CompanyVerificationStatusResponse | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [missingReason, setMissingReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [capturedPhotoUrl, setCapturedPhotoUrl] = useState<string | null>(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cameraModalRef = useRef<HTMLDivElement>(null);
  const photoModalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void adminSignupGateway.getVerification().then(setStatus);
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  useModalFocusTrap(cameraModalOpen, cameraModalRef, closeCameraModal);
  useModalFocusTrap(photoModalOpen, photoModalRef, () => setPhotoModalOpen(false));

  async function handleDocumentTypeChange(value: string) {
    setDocumentType(value);
    if (value !== 'No Documents Yet') {
      const updated = await adminSignupGateway.setDocumentType(value, null);
      setStatus(updated);
    }
  }

  async function handleMissingReasonChange(value: string) {
    setMissingReason(value);
    const updated = await adminSignupGateway.setDocumentType('No Documents Yet', value);
    setStatus(updated);
  }

  async function handleFileSelected(file: File) {
    setError(null);
    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`${file.name} exceeds the 10 MB limit.`);
      return;
    }
    if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
      setError(`${file.name} is not a supported format (PDF, JPG, PNG).`);
      return;
    }
    try {
      await adminSignupGateway.uploadDocument(file);
      setStatus(await adminSignupGateway.getVerification());
    } catch (err) {
      setError(friendlyErrorMessage(err, `Could not upload ${file.name}.`));
    }
  }

  function stopStream() {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }

  async function openCameraModal() {
    setError(null);
    setCameraError(null);
    setCameraModalOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch {
      setCameraError('Camera access is unavailable. Check your browser permissions and try again.');
    }
  }

  function closeCameraModal() {
    stopStream();
    setCameraModalOpen(false);
  }

  async function capturePhoto() {
    const video = videoRef.current;
    if (!video) return;
    setCapturing(true);
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      if (!blob) {
        setCapturing(false);
        return;
      }
      try {
        const file = new File([blob], 'live-photo.png', { type: 'image/png' });
        const updated = await adminSignupGateway.uploadLivePhoto(file);
        setStatus(updated);
        setCapturedPhotoUrl(canvas.toDataURL('image/png'));
        closeCameraModal();
      } catch (err) {
        setCameraError(friendlyErrorMessage(err, 'Could not save the captured photo.'));
      } finally {
        setCapturing(false);
      }
    }, 'image/png');
  }

  async function handleSkip() {
    setStatus(await adminSignupGateway.skipVerification());
    goNext();
  }

  function handleContinue() {
    if (!status) return;
    if (status.status === 'Submitted' || status.status === 'Skipped') {
      goNext();
      return;
    }
    if (status.documents.length === 0) {
      setError('Upload at least one supported business document before continuing, or click Skip for now.');
      return;
    }
    if (!status.hasLivePhoto) {
      setError('Capture a live photo to complete Identity Verification, or click Skip for now to continue with verification pending.');
      return;
    }
  }

  return (
    <div className="screen active" data-testid="identity-verification-step">
      <div className="screen-icon" aria-hidden="true">
        🏢
      </div>
      {/* The approved FINAL HTML reuses this literal visible heading for both Step 1's screens — see the
          Admin Sign Up implementation report's UX/DESIGN GAP note. Visible copy is intentionally
          unchanged; aria-label gives assistive technology a distinct accessible name from the Company
          Information screen's own (identical-looking) heading, so the two are announced differently. */}
      <h2 aria-label={accessibleHeading}>{heading}</h2>
      <div className="substepper" aria-hidden="true">
        <div className="substep done">
          <span className="dot">1</span>{firstSubstepLabel}
        </div>
        <span className="subline" />
        <div className="substep active">
          <span className="dot">2</span>Identity Verification
        </div>
      </div>

      <div className="section-heading">Document Verification</div>
      <div className="field-grid">
        <div className="field full">
          <label htmlFor="documentType">
            Document Type <span className="req">*</span>
          </label>
          <select className="control" id="documentType" value={documentType} onChange={(e) => void handleDocumentTypeChange(e.target.value)}>
            <option value="">Select</option>
            {DOCUMENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>

        {documentType === 'No Documents Yet' && (
          <div className="field">
            <label htmlFor="missingReason">Reasons for missing documents</label>
            <select className="control" id="missingReason" value={missingReason} onChange={(e) => void handleMissingReasonChange(e.target.value)}>
              <option value="">Select reason</option>
              {MISSING_REASONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="upload-zone">
        <input
          type="file"
          id="documentUpload"
          accept="application/pdf,image/jpeg,image/png"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFileSelected(file);
            e.target.value = '';
          }}
        />
        <div>
          <div className="upload-icon" aria-hidden="true">
            ⬆
          </div>
          <div className="prompt">
            <label htmlFor="documentUpload">Click to upload</label> or drag and drop
          </div>
          <small>PDF, JPG, PNG · Max 10 MB</small>
        </div>
      </div>

      {status && status.documents.length > 0 && (
        <>
          <div className="file-list-title">Uploaded Files ({status.documents.length}):</div>
          <div className="file-strip">
            {status.documents.map((d) => (
              <div className="file-card" key={d.id}>
                <span className="file-icon" aria-hidden="true">
                  📄
                </span>
                <span className="name">
                  {d.originalFileName} — {(d.sizeBytes / 1048576).toFixed(1)} MB
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="divider" />
      <div className="section-heading">Facial Verification</div>
      <div className="section-label">Take a clear photo of yourself to confirm you are the authorized representative</div>

      {!status?.hasLivePhoto && (
        <>
          <button className="camera-btn" type="button" onClick={() => void openCameraModal()}>
            <span aria-hidden="true">📷</span>
            <span>Open Camera</span>
          </button>
          <div className="camera-status">
            <span className="info-icon">i</span>No photo uploaded yet.
          </div>
        </>
      )}

      {status?.hasLivePhoto && (
        <div className="live-photo">
          <div className="live-check">✓</div>
          <div className="live-copy">
            <strong>Live Photo Captured</strong>
            <small>Make sure that the uploaded photo is clear and visible.</small>
          </div>
          <div className="photo-actions">
            <button className="small-btn light" type="button" onClick={() => void openCameraModal()}>
              Retake
            </button>
            <button className="small-btn dark" type="button" onClick={() => setPhotoModalOpen(true)} disabled={!capturedPhotoUrl}>
              View Photo
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="error-line" role="alert">
          <span className="info-icon">i</span>
          <span>{error}</span>
        </p>
      )}

      <div className="actions">
        <button className="skip" type="button" onClick={() => void handleSkip()}>
          Skip for now
        </button>
        <button className="btn btn-back" type="button" onClick={goBack}>
          Go Back
        </button>
        <button className="btn btn-primary" type="button" onClick={handleContinue}>
          Save and Continue
        </button>
      </div>

      {cameraModalOpen && (
        <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && closeCameraModal()}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="cameraModalTitle" ref={cameraModalRef}>
            <div className="modal-head">
              <h3 id="cameraModalTitle">Live Facial Verification</h3>
              <button className="modal-close" type="button" aria-label="Close" onClick={closeCameraModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="camera-box">
                {cameraError ? (
                  <div className="camera-fallback">{cameraError}</div>
                ) : (
                  <video ref={videoRef} autoPlay playsInline muted />
                )}
              </div>
              <div className="modal-actions">
                <button className="btn btn-back" type="button" onClick={closeCameraModal}>
                  Cancel
                </button>
                <button className="btn btn-primary" type="button" onClick={() => void capturePhoto()} disabled={!!cameraError || capturing}>
                  {capturing ? 'Capturing…' : 'Capture Photo'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {photoModalOpen && (
        <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && setPhotoModalOpen(false)}>
          <div className="modal" role="dialog" aria-modal="true" aria-labelledby="photoModalTitle" ref={photoModalRef}>
            <div className="modal-head">
              <h3 id="photoModalTitle">Captured Live Photo</h3>
              <button className="modal-close" type="button" aria-label="Close" onClick={() => setPhotoModalOpen(false)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              {capturedPhotoUrl && <img className="photo-preview" src={capturedPhotoUrl} alt="Captured live verification photo" />}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
